// Column widths and row heights for a matrix ("table") field.
//
// A table used to be described by ONE horizontal gap and ONE vertical gap, so
// every column came out the same width. Scanned forms are not like that — on
// the Threshold Concrete report "Delivery Ticket No." is about three times the
// width of "Slump (in)" — which made lining the cells up with the printed boxes
// not merely fiddly but impossible: no pair of gaps fits an uneven grid.
//
// A table now carries a width per column and a height per row. Both are
// OPTIONAL: when absent (every table saved before this change) each entry falls
// back to the old uniform gap, so existing forms render exactly as they did.

export interface MatrixSizes {
  columnWidths: number[];
  rowHeights: number[];
}

const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const count = (v: any, fallback = 1): number => {
  const n = Math.floor(num(v));
  return n > 0 ? n : fallback;
};

// A degenerate size collapses cells on top of each other and makes the table
// impossible to grab, so keep every track visible.
export const MIN_TRACK_MM = 1;

/**
 * Resolve a stored size list to exactly `n` positive numbers.
 *
 * Always returns `n` entries, so changing the column count is self-healing: new
 * tracks appear at the uniform gap and surplus ones are dropped.
 */
export const resolveSizes = (raw: any, n: number, fallbackMm: number): number[] => {
  const fallback = Math.max(MIN_TRACK_MM, num(fallbackMm) || MIN_TRACK_MM);
  const list = Array.isArray(raw) ? raw : [];
  const out: number[] = [];
  for (let i = 0; i < n; i += 1) {
    const v = num(list[i]);
    out.push(v >= MIN_TRACK_MM ? v : fallback);
  }
  return out;
};

/** Every column width for a field, in mm, honouring the legacy uniform gap. */
export const columnWidthsOf = (field: any): number[] =>
  resolveSizes(field?.columnWidths, count(field?.columns), num(field?.gapHorizontal));

/** Every row height for a field, in mm, honouring the legacy uniform gap. */
export const rowHeightsOf = (field: any): number[] =>
  resolveSizes(field?.rowHeights, count(field?.rows), num(field?.gapVertical));

/**
 * Distance from the grid origin to the START of each track.
 *
 * Length is `sizes.length + 1`: the extra entry is the far edge, which is what
 * the table's full footprint and its last divider are drawn from.
 */
export const offsetsOf = (sizes: number[]): number[] => {
  const out = [0];
  sizes.forEach(size => out.push(out[out.length - 1] + size));
  return out;
};

export const totalOf = (sizes: number[]): number =>
  sizes.reduce((sum, size) => sum + size, 0);

/** Replace one track's size, clamped so it can never collapse. */
export const withSize = (sizes: number[], index: number, mm: number): number[] =>
  sizes.map((size, i) => (i === index ? Math.max(MIN_TRACK_MM, mm) : size));

/** Scale every track — used when the whole table is resized by its corner. */
export const scaleSizes = (sizes: number[], factor: number): number[] => {
  if (!Number.isFinite(factor) || factor <= 0) return sizes;
  return sizes.map(size => Math.max(MIN_TRACK_MM, size * factor));
};

/** Equal tracks totalling `totalMm` — the "distribute evenly" action. */
export const evenSizes = (n: number, totalMm: number): number[] => {
  const each = Math.max(MIN_TRACK_MM, num(totalMm) / Math.max(1, n));
  return new Array(Math.max(0, n)).fill(each);
};

export const roundSizes = (sizes: number[]): string[] =>
  sizes.map(size => `${Math.round(size * 10) / 10}`);
